/**
 * ClinicUserTags — admin-managed clinic tags (global registry with colors).
 *
 * Single source of truth for the tag palette: TAG_COLORS keys map to the
 * --nivut-<key>[-bg|-border] CSS tokens defined on `.nivut-scope`
 * (ClinicUserTags.css). Every tag chip / dot / swatch across the NiV
 * clinics tab consumes these tokens — never restyle a tag ad-hoc.
 */

import { Check } from "@phosphor-icons/react";
import "./ClinicUserTags.css";

export const TAG_COLORS = [
  { key: "mint", label: "מנטה" },
  { key: "blue", label: "כחול" },
  { key: "violet", label: "סגול" },
  { key: "amber", label: "ענבר" },
  { key: "rose", label: "ורוד עמוק" },
  { key: "teal", label: "טורקיז" },
  { key: "slate", label: "אפור" },
  { key: "orange", label: "כתום" },
  { key: "pink", label: "ורוד" },
  { key: "indigo", label: "אינדיגו" },
];

const safeColor = (color) => (TAG_COLORS.some((c) => c.key === color) ? color : "slate");

export function UserTagDot({ color, size = 8, className = "" }) {
  return (
    <span
      className={`nivut-dot nivut-c-${safeColor(color)} ${className}`.trim()}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

/** Square chip: color dot + label. `pop` = play the pop-in animation. */
export function UserTagChip({ tag, pop = false, onClick = null, title = "" }) {
  if (!tag) return null;
  const cls = `nivut-chip nivut-c-${safeColor(tag.color)}${pop ? " nivut-pop" : ""}${onClick ? " is-clickable" : ""}`;
  const inner = (
    <>
      <UserTagDot color={tag.color} size={6} />
      <span className="nivut-chip-label">{tag.label}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick} title={title || tag.label}>
        {inner}
      </button>
    );
  }
  return (
    <span className={cls} title={title || tag.label}>
      {inner}
    </span>
  );
}

/** Square swatch grid for picking a tag color. */
export function ColorSwatchPicker({ value, onChange, size = 22 }) {
  return (
    <div className="nivut-swatches" role="radiogroup" aria-label="צבע תגית">
      {TAG_COLORS.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={c.label}
            title={c.label}
            className={`nivut-swatch nivut-c-${c.key}${active ? " is-active" : ""}`}
            style={{ width: size, height: size }}
            onClick={() => onChange(c.key)}
          >
            {active && <Check size={12} weight="bold" />}
          </button>
        );
      })}
    </div>
  );
}
